/**
 * O núcleo econômico: quem é o mesmo ator que esta entidade, por qual via, e sob qual régua.
 *
 * **O limiar é um controle, não uma configuração escondida.** O mesmo conjunto de empresas é um
 * grupo a 0,60 e três a 0,85 — quem lê precisa poder mexer na régua e ver o agrupamento mudar, ou
 * o número vira uma afirmação que não se pode conferir. Por isso o seletor fica no topo, e não num
 * menu de ajustes.
 *
 * Cada membro mostra **a razão de ter entrado** (a via e o detalhe: «sócio em comum: M Sader»),
 * porque uma lista de empresas sem a razão ao lado pede confiança cega. E o que foi ignorado por
 * ser hub aparece embaixo, contado: truncar em silêncio é apresentar um recorte como se fosse o
 * conjunto.
 */
import { Layers } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip, Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';

type Membro = {
  tipo: string;
  id: string;
  rotulo: string;
  rota: string;
  confianca: number;
  via: string | null;
  detalhe: string | null;
};

type RespostaNucleo = {
  semente: [string, string];
  limiar: number;
  hub_maximo: number;
  truncado: boolean;
  tamanho: number;
  membros: Membro[];
  hubs_ignorados: Record<string, number>;
};

/** As réguas oferecidas. Nomeadas pelo que ADMITEM, não pelo número — «0,60» não diz nada a quem
 *  lê; «admite endereço em comum» diz exatamente o que está sendo aceito como prova. */
const REGUAS = [
  { valor: 0.5, rotulo: 'Frouxa', ajuda: 'admite telefone, endereço ou parentesco em comum' },
  { valor: 0.75, rotulo: 'Padrão', ajuda: 'exige e-mail em comum ou laço societário — família não entra' },
  { valor: 0.85, rotulo: 'Estrita', ajuda: 'só laço societário: sócio, participação ou mesma raiz' },
];

/** Como cada via se lê na tela, e o quanto ela vale. Medida e inferência não podem ter a mesma
 *  cara — é a mesma disciplina da etiqueta de método dos achados de rastreamento. */
const VIA: Record<string, { rotulo: string; classe: string }> = {
  raiz_cnpj: { rotulo: 'mesma raiz de CNPJ', classe: 'border-success/40 text-success' },
  participacao: { rotulo: 'participação societária', classe: 'border-success/40 text-success' },
  socio_comum: { rotulo: 'sócio em comum', classe: 'border-success/40 text-success' },
  // Parentesco DECLARADO. Nenhum grau de família alcança 0,75, então esta etiqueta só aparece
  // quando o operador afrouxa a régua — e o detalhe da aresta diz qual é o laço e de quem.
  familia: { rotulo: 'parentesco declarado', classe: 'border-border text-text-secondary' },
  email: { rotulo: 'mesmo e-mail', classe: 'border-border text-text-secondary' },
  endereco: { rotulo: 'mesmo endereço', classe: 'border-warning/40 text-warning' },
  telefone: { rotulo: 'mesmo telefone', classe: 'border-warning/40 text-warning' },
};

export function PainelNucleo({ ownerTipo, ownerId }: { ownerTipo: string; ownerId: string }) {
  const [limiar, setLimiar] = useState(0.75);
  const [dados, setDados] = useState<RespostaNucleo | null>(null);
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async () => {
    setCarregando(true);
    const r = await api.get<RespostaNucleo>(
      `/cadastro/nucleo/${ownerTipo}/${ownerId}?limiar=${limiar}`,
    );
    setCarregando(false);
    if (r.ok) setDados(r.dados);
    else toast.error(r.erro);
  }, [ownerTipo, ownerId, limiar]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // A semente é sempre membro dela mesma; um núcleo de UM é "não achei mais ninguém".
  const outros = (dados?.membros ?? []).filter((m) => m.id !== ownerId);
  const hubs = Object.entries(dados?.hubs_ignorados ?? {});

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Layers className="size-4 text-accent" />
          Núcleo econômico
        </CardTitle>
        <p className="text-[13px] leading-[1.5] text-text-secondary">
          Quem é o mesmo ator econômico que esta entidade — e por quê. Nada é fundido: as entidades
          continuam de pé, o que existe é o agrupamento.
        </p>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-2">
          {REGUAS.map((r) => (
            <button
              key={r.valor}
              type="button"
              title={r.ajuda}
              onClick={() => setLimiar(r.valor)}
              className={`tipo-rotulo rounded-md border px-2 py-1 transition-colors ${
                limiar === r.valor
                  ? 'border-accent text-accent'
                  : 'border-border text-text-tertiary hover:text-text'
              }`}
            >
              {r.rotulo}
            </button>
          ))}
          <span className="tipo-corpo-pequeno text-text-tertiary">
            {REGUAS.find((r) => r.valor === limiar)?.ajuda}
          </span>
        </div>

        {carregando ? (
          <Spinner />
        ) : !outros.length ? (
          <p className="tipo-corpo-pequeno text-text-tertiary">
            Sozinha nesta régua. Nenhuma outra entidade se liga a esta com confiança de ao menos{' '}
            {limiar.toFixed(2).replace('.', ',')} — o que não quer dizer que não haja ligação
            nenhuma: uma régua mais frouxa pode encontrar.
          </p>
        ) : (
          <div className="flex flex-col">
            {outros.map((m) => {
              const via = m.via ? VIA[m.via] : undefined;
              return (
                <div
                  key={`${m.tipo}:${m.id}`}
                  className="flex flex-wrap items-baseline gap-2 border-t border-border py-2 first:border-t-0"
                >
                  <Link to={m.rota} className="font-semibold text-accent hover:underline">
                    {m.rotulo}
                  </Link>
                  {via && <Chip className={via.classe}>{via.rotulo}</Chip>}
                  <span className="tipo-corpo-pequeno text-text-tertiary">
                    {m.detalhe} · confiança {m.confianca.toFixed(2).replace('.', ',')}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {dados?.truncado && (
          <p className="tipo-corpo-pequeno text-warning">
            O núcleo é maior do que coube nesta leitura. Um núcleo grande costuma ser sinal de que a
            régua está frouxa demais para a pergunta.
          </p>
        )}

        {hubs.length > 0 && (
          <details className="border-t border-border pt-2">
            <summary className="tipo-rotulo cursor-pointer select-none text-text-secondary hover:text-text">
              Ignorados por serem compartilhados demais ({hubs.length})
            </summary>
            <p className="tipo-corpo-pequeno mt-2 text-text-tertiary">
              Um endereço com dezenas de empresas é um escritório virtual, e um sócio de dezenas de
              sociedades é um administrador profissional. Nenhum dos dois liga ninguém a ninguém —
              entram aqui, contados, em vez de gerar centenas de ligações falsas.
            </p>
            <div className="mt-2 flex flex-col gap-1">
              {hubs.map(([rotulo, n]) => (
                <p key={rotulo} className="tipo-corpo-pequeno text-text-tertiary">
                  · {rotulo} — {n} entidades
                </p>
              ))}
            </div>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
